import {
  FormControls,
  FormField,
  TextField,
} from '@elementary/common-ui/components';
import { Typography } from '@mui/material';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';

type AnomalyDirection = 'BOTH' | 'DROP' | 'SPIKE';

const validateRequired = (value: unknown): true | string => {
  if (value == null || Number.isNaN(Number(value))) return 'Required';
  if (Number(value) <= 0) return 'Must be > 0';
  return true;
};

const validateDrop = (value: unknown): true | string => {
  const required = validateRequired(value);
  if (required !== true) return required;
  if (Number(value) > 100) return 'Must be ≤ 100';
  return true;
};

type ThresholdRowProps<T extends FieldValues> = {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  validate: (value: unknown) => true | string;
  readOnly?: boolean;
  shouldUnregister?: boolean;
};

const ThresholdRow = <T extends FieldValues>({
  control,
  name,
  label,
  validate,
  readOnly,
  shouldUnregister,
}: ThresholdRowProps<T>) => (
  <FormControls.Row>
    <Typography component="span" sx={{ minWidth: '128px' }}>
      {label}
    </Typography>
    <Controller
      control={control}
      name={name}
      shouldUnregister={shouldUnregister}
      rules={{ validate }}
      render={({ field, fieldState }) => (
        <FormField errorMessage={fieldState.error?.message}>
          <TextField
            {...field}
            sx={{ width: '120px' }}
            numeric
            disabled={readOnly}
            error={!!fieldState.error}
            value={field.value ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              field.onChange(v === '' ? null : Number(v));
            }}
          />
        </FormField>
      )}
    />
    <Typography component="span">%</Typography>
  </FormControls.Row>
);

type Props<T extends FieldValues> = {
  control: Control<T>;
  anomalyDirection: AnomalyDirection | undefined;
  spikeFieldName: FieldPath<T>;
  dropFieldName: FieldPath<T>;
  readOnly?: boolean;
  shouldUnregister?: boolean;
};

const PercentageThresholdField = <T extends FieldValues>({
  control,
  anomalyDirection,
  spikeFieldName,
  dropFieldName,
  readOnly,
  shouldUnregister,
}: Props<T>) => {
  const showSpike = anomalyDirection === 'SPIKE' || anomalyDirection === 'BOTH';
  const showDrop = anomalyDirection === 'DROP' || anomalyDirection === 'BOTH';

  return (
    <>
      {showSpike && (
        <ThresholdRow
          control={control}
          name={spikeFieldName}
          label="Spike threshold:"
          validate={validateRequired}
          readOnly={readOnly}
          shouldUnregister={shouldUnregister}
        />
      )}
      {showDrop && (
        <ThresholdRow
          control={control}
          name={dropFieldName}
          label="Drop threshold:"
          validate={validateDrop}
          readOnly={readOnly}
          shouldUnregister={shouldUnregister}
        />
      )}
    </>
  );
};

export default PercentageThresholdField;
